import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';

export interface LoginFormProps {
  onLogin: (email: string, password: string) => Promise<{ error?: Error; nextStep?: string }>;
  onCompleteNewPassword?: (password: string) => Promise<{ error?: Error }>;
  appName?: string;
  websiteUrl?: string;
  className?: string;
}

export function LoginForm({
  onLogin,
  onCompleteNewPassword,
  appName = "Next Store",
  websiteUrl,
  className
}: LoginFormProps) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState<'LOGIN' | 'NEW_PASSWORD'>('LOGIN');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for success message from signup
    if (searchParams.get('signup') === 'success') {
      setSuccessMessage('Account created successfully! Please sign in.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (step === 'LOGIN') {
        const result = await onLogin(email, password);

        if (result.error) {
          setError(result.error.message || 'Failed to sign in');
          setIsLoading(false);
          return;
        }

        if (result.nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setStep('NEW_PASSWORD');
          setIsLoading(false);
          return;
        }

        // Success - redirect will be handled by parent
        const redirect = searchParams.get('redirect') || '/';
        navigate(redirect);
      } else {
        // NEW_PASSWORD step
        if (newPassword !== confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }

        if (newPassword.length < 8) {
          setError('Password must be at least 8 characters long');
          setIsLoading(false);
          return;
        }

        if (onCompleteNewPassword) {
          const result = await onCompleteNewPassword(newPassword);
          if (result.error) {
            setError(result.error.message || 'Failed to set new password');
            setIsLoading(false);
            return;
          }

          // Success - redirect to home
          const redirect = searchParams.get('redirect') || '/';
          navigate(redirect);
        } else {
          setError('System error: new password handler not provided');
          setIsLoading(false);
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-surface ${className || ''}`}>
      <div className="max-w-md w-full mx-4 relative">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <Card className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-lg mb-4 shadow-sm">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
            </div>
            <h1 className="heading-lg mb-2">
              {t('welcome')}
            </h1>
            <p className="caption">
              {appName}
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-success-lighter border border-success-light rounded-md">
              <p className="text-sm text-success-dark text-center font-medium">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-critical-lighter border border-critical-light rounded-md">
              <p className="text-sm text-critical-dark text-center font-medium">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 'LOGIN' ? (
              <>
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-text mb-2"
                  >
                    {t('email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-field"
                    placeholder="you@yourstore.com"
                    disabled={isLoading}
                  />
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-text"
                    >
                      {t('password')}
                    </label>
                    <a href="#" className="text-sm text-primary hover:underline">
                      {t('forgotPassword')}
                    </a>
                  </div>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input-field"
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="bg-primary/5 p-4 rounded-lg mb-4">
                  <p className="text-sm text-primary-dark">
                    <strong>New Password Required</strong><br />
                    Since this is your first time logging in (or your password was reset), please choose a new permanent password.
                  </p>
                </div>

                {/* New Password */}
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-text mb-2"
                  >
                    {t('newPassword') || 'New Password'}
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="input-field"
                    placeholder="••••••••"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>

                {/* Confirm Password */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-text mb-2"
                  >
                    {t('confirmPassword') || 'Confirm New Password'}
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="input-field"
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                </div>
              </>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t('loading')}
                </span>
              ) : (
                step === 'LOGIN' ? t('signIn') : (t('updatePassword') || 'Set New Password')
              )}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 pt-6 border-t border-border-subdued text-center">
            <p className="text-sm text-text-subdued">
              {t('dontHaveAccount')}{' '}
              <Link to="/signup" className="text-primary font-medium hover:underline">
                {t('signUp')}
              </Link>
            </p>
          </div>
        </Card>

        {/* Back to website */}
        {websiteUrl && (
          <div className="mt-6 text-center">
            <a
              href={websiteUrl}
              className="text-sm text-text-subdued hover:text-text"
            >
              ← Back to website
            </a>
          </div>
        )}
      </div>
    </div>
  );
}













